import { Candidate } from "@Views/Screening/utility/apiAgent";

/**
 * Shared AI "autofill from documents" behaviour for the candidate forms.
 *
 * A host component must provide:
 *   - data:    basicInfo (Object), payrollInfo (Object), addressInfo (Array),
 *              candidateHistoryInfo (Array), trainingInfo (Array)
 *   - method:  dateFormatter(date)
 *   - ref:     cvUpload (a hidden <input type="file">) - only needed for the
 *              single-CV entry point (triggerCvUpload/handleCvUpload)
 *   - $toastr  (global)
 * Optional, used only when present: showCompletedStatus, is_consent_modal,
 * emailAddress, getEmailFromQueryString(), applyRequiredHighlight().
 *
 * Two entry points are provided:
 *   - Single CV upload (chooseAiFill / handleCvUpload) — no persistence, just
 *     returns parsed fields for the candidate to review.
 *   - Full document pack with consent (openAiDocumentFlow / processAiDocuments)
 *     — uploads CV + supporting documents, persists them against the
 *     applicant, and fills in whatever fields the AI could read.
 *
 * Every extracted value only fills empty fields — it never overwrites data
 * that is already present, whether entered by the candidate or an admin.
 * Each section is applied independently and fail-soft: a problem parsing one
 * section (e.g. a malformed date in employment) can never prevent the other
 * tabs from being filled.
 */
export const aiCandidateFillMixin = {
  data() {
    return {
      aiExtracting: false,
      aiImportSummary: '',
      aiWarnings: [],
      fillChoiceModalVisible: false,
      aiFillPrompted: false,
      aiFillPending: false,
      aiConsentModalVisible: false,
      aiDocumentUploadModalVisible: false,
      aiConsentAccepted: false,
      aiHasDrivingLicense: false,
      aiDocuments: this.emptyAiDocuments(),
      aiDocumentErrors: [],
      aiUploadInputKey: 0,
    };
  },
  computed: {
    aiDocumentUploadFields() {
      const cvAccept = '.pdf,.doc,.docx,.odt,.rtf,.txt';
      const documentAccept = '.pdf,.jpg,.jpeg,.png,.gif,.bmp,.webp';
      const fields = [
        {
          key: 'cv',
          label: 'CV',
          help: 'PDF, Word, OpenDocument, RTF or text. Max 10 MB.',
          accept: cvAccept,
          required: true,
        },
        {
          key: 'passport',
          label: 'Passport / Right-to-Work Proof',
          help: 'Passport photo page or right-to-work document.',
          accept: documentAccept,
          required: false,
        },
        {
          key: 'bankStatement',
          label: 'Bank Statement',
          help: 'Recent statement that clearly shows bank details.',
          accept: documentAccept,
          required: false,
        },
        {
          key: 'utilityBill_1',
          label: 'Utility Bill (A)',
          help: 'First proof of address.',
          accept: documentAccept,
          required: false,
        },
        {
          key: 'utilityBill_2',
          label: 'Utility Bill (B)',
          help: 'Second proof of address.',
          accept: documentAccept,
          required: false,
        },
        {
          key: 'siaLicense',
          label: 'SIA Licence',
          help: 'Front/back or a clear SIA portal screenshot.',
          accept: documentAccept,
          required: false,
        },
      ];

      if (this.aiHasDrivingLicense) {
        fields.push({
          key: 'driving_license',
          label: 'Driving Licence',
          help: 'Front and back, if available.',
          accept: documentAccept,
          required: false,
        });
      }

      return fields;
    },
  },
  methods: {
    /* ----- entry points -------------------------------------------------- */

    openFillChoiceModal() {
      this.fillChoiceModalVisible = true;
    },

    chooseManualFill() {
      this.fillChoiceModalVisible = false;
    },

    chooseAiFill() {
      this.fillChoiceModalVisible = false;
      this.$nextTick(() => this.triggerCvUpload());
    },

    // Open the file picker directly (used by the candidate-detail "Autofill from CV" button).
    triggerCvUpload() {
      if (this.aiExtracting) return;
      if (this.$refs.cvUpload) this.$refs.cvUpload.click();
    },

    maybeOfferAiFill() {
      // Auto-offer the AI/manual choice once per page load, unless the form is already completed.
      if (this.aiFillPrompted || this.showCompletedStatus) return;
      // Never stack on top of the Policy & Declaration consent modal; defer until it is dismissed.
      if (this.is_consent_modal) {
        this.aiFillPending = true;
        return;
      }
      this.aiFillPrompted = true;
      this.fillChoiceModalVisible = true;
    },

    // Open the consent + multi-document upload flow directly (used by the
    // admin "Autofill with AI" button on the Candidate Detail screen).
    openAiDocumentFlow() {
      if (this.aiExtracting) return;
      this.syncAiDrivingLicenseSelection();
      if (this.aiConsentAccepted) {
        this.aiDocumentUploadModalVisible = true;
        return;
      }
      this.aiConsentModalVisible = true;
    },

    acceptAiConsent() {
      this.aiConsentAccepted = true;
      this.aiConsentModalVisible = false;
      this.syncAiDrivingLicenseSelection();
      this.aiDocumentUploadModalVisible = true;
    },

    syncAiDrivingLicenseSelection() {
      if (this.basicInfo.is_driving_license === 'yes' || this.basicInfo.is_driving_license === true || this.basicInfo.is_driving_license === 1) {
        this.aiHasDrivingLicense = true;
      }
    },

    /* ----- upload + extract (single CV) ---------------------------------- */

    async handleCvUpload(event) {
      const document = event.target.files && event.target.files[0];
      if (!document) return;

      this.aiExtracting = true;
      this.aiImportSummary = '';
      this.aiWarnings = [];

      const formData = new FormData();
      formData.append('document', document);

      try {
        const response = await Candidate.extractCv(formData);
        const extracted = response?.data?.data || {};
        const result = this.applyAiCandidateData(extracted);

        this.aiWarnings = Array.isArray(extracted.warnings) ? extracted.warnings : [];
        this.aiImportSummary = this.buildAiImportSummary(result);
        this.$toastr.s(response?.data?.message || 'CV information imported. Please review it carefully.');
      } catch (error) {
        const validationMessage = error?.response?.data?.errors?.document?.[0];
        const message = validationMessage || error?.response?.data?.message || 'The CV could not be processed. Please enter the information manually.';
        this.$toastr.e(message);
      } finally {
        this.aiExtracting = false;
        if (this.$refs.cvUpload) this.$refs.cvUpload.value = '';
      }
    },

    buildAiImportSummary(result) {
      const parts = [];
      if (result.fields) parts.push(`${result.fields} field${result.fields === 1 ? '' : 's'}`);
      if (result.entries) parts.push(`${result.entries} list entr${result.entries === 1 ? 'y' : 'ies'}`);

      return parts.length
        ? `Imported ${parts.join(', ')}.`
        : 'No new information could be imported from this document.';
    },

    /* ----- upload + extract (full document pack with consent) ------------ */

    emptyAiDocuments() {
      return {
        cv: null,
        passport: null,
        bankStatement: null,
        utilityBill_1: null,
        utilityBill_2: null,
        siaLicense: null,
        driving_license: null,
      };
    },

    handleAiDocumentSelection(key, event) {
      const file = event.target.files && event.target.files[0] ? event.target.files[0] : null;
      this.$set(this.aiDocuments, key, file);
      this.aiDocumentErrors = [];
    },

    cancelAiDocumentUpload() {
      this.aiDocumentUploadModalVisible = false;
      this.aiDocumentErrors = [];
    },

    resetAiDocumentUpload() {
      this.aiDocuments = this.emptyAiDocuments();
      this.aiDocumentErrors = [];
      this.aiHasDrivingLicense = false;
      this.aiUploadInputKey++;
    },

    validateAiDocuments() {
      const errors = [];
      this.aiDocumentUploadFields.forEach((document) => {
        if (document.required && !this.aiDocuments[document.key]) {
          errors.push(`${document.label} is required.`);
        }
      });
      this.aiDocumentErrors = errors;
      return errors.length === 0;
    },

    async processAiDocuments() {
      if (!this.validateAiDocuments()) {
        return;
      }

      const email = this.basicInfo.email
        || this.emailAddress
        || (typeof this.getEmailFromQueryString === 'function' ? this.getEmailFromQueryString() : null)
        || localStorage.getItem('candidate-email');

      this.aiExtracting = true;
      this.aiImportSummary = '';
      this.aiWarnings = [];

      const formData = new FormData();
      if (email) {
        formData.append('email', email);
      }

      Object.entries(this.aiDocuments).forEach(([key, file]) => {
        if (file) {
          formData.append(`documents[${key}]`, file);
        }
      });

      try {
        const response = await Candidate.extractDocuments(formData);
        const extracted = response?.data?.data || {};
        const storedFiles = response?.data?.files || [];
        const result = this.applyAiCandidateData(extracted);
        this.applyUploadedAiFiles(storedFiles);

        this.aiWarnings = Array.isArray(extracted.warnings) ? extracted.warnings : [];
        this.aiImportSummary = `${result.fields} field${result.fields === 1 ? '' : 's'}, ${result.entries} list entr${result.entries === 1 ? 'y' : 'ies'}, and ${storedFiles.length} file${storedFiles.length === 1 ? '' : 's'} imported.`;
        this.$toastr.s(response?.data?.message || 'Documents imported. Please review all fields carefully.');

        this.aiDocumentUploadModalVisible = false;
        this.resetAiDocumentUpload();
        if (typeof this.applyRequiredHighlight === 'function') {
          this.aiHighlightActive = true;
          this.$nextTick(() => this.applyRequiredHighlight());
        }
      } catch (error) {
        const errors = error?.response?.data?.errors;
        if (errors) {
          this.aiDocumentErrors = Object.values(errors).flat();
        }
        const message = this.aiDocumentErrors[0] || error?.response?.data?.message || 'The documents could not be processed. Please enter the information manually.';
        this.$toastr.e(message);
      } finally {
        this.aiExtracting = false;
      }
    },

    /* ----- field mapping (fail-soft per section, fill-empty-only) -------- */

    applyAiCandidateData(extracted) {
      let fields = 0;
      let entries = 0;

      try {
        Object.entries(extracted.basic_info || {}).forEach(([key, value]) => {
          if (!this.hasValue(value) || this.hasValue(this.basicInfo[key])) return;
          this.$set(this.basicInfo, key, value);
          fields++;
        });
      } catch (e) { /* keep going so other tabs still fill */ }

      try {
        if (this.payrollInfo) {
          Object.entries(extracted.payroll || {}).forEach(([key, value]) => {
            if (!this.hasValue(value) || this.hasValue(this.payrollInfo[key])) return;
            this.$set(this.payrollInfo, key, key === 'sort_code' ? this.normalizeSortCode(value) : value);
            fields++;
          });
        }
      } catch (e) { /* keep going */ }

      try {
        entries += this.applyAiLicenses(extracted.licenses || []);
      } catch (e) { /* keep going */ }

      try {
        (extracted.addresses || []).forEach((address) => {
          const normalized = this.normalizeAiAddress(address);
          if (!this.hasAddressIdentity(normalized) || this.hasDuplicateAddress(normalized)) return;
          this.addressInfo.push(normalized);
          entries++;
        });
      } catch (e) { /* keep going */ }

      try {
        (extracted.employment || []).forEach((employment) => {
          const normalized = this.normalizeAiHistory(employment, 0);
          if (!this.hasValue(normalized.company_name) || this.hasDuplicateHistory(normalized)) return;
          this.candidateHistoryInfo.push(normalized);
          entries++;
        });
      } catch (e) { /* keep going */ }

      try {
        (extracted.education || []).forEach((education) => {
          const normalized = this.normalizeAiHistory(education, 1);
          if (!this.hasValue(normalized.institute) || this.hasDuplicateHistory(normalized)) return;
          this.candidateHistoryInfo.push(normalized);
          entries++;
        });
      } catch (e) { /* keep going */ }

      try {
        (extracted.training || []).forEach((training) => {
          const normalized = { ...training, screening_status: [] };
          if (!this.hasValue(normalized.title) || this.hasDuplicateTraining(normalized)) return;
          this.trainingInfo.push(normalized);
          entries++;
        });
      } catch (e) { /* keep going */ }

      return { fields, entries };
    },

    normalizeAiHistory(entry, type) {
      const safeFormat = (date) => {
        if (!date) return '';
        try { return this.dateFormatter(date); } catch (e) { return ''; }
      };
      const current = !!entry.current_status;
      return {
        ...entry,
        type,
        current_status: current,
        statusable_type: type === 0
          ? 'App\\Models\\Screening\\ApplicantEmployment'
          : 'App\\Models\\Screening\\ApplicantEducation',
        screening_status: [],
        start_date_formatted: safeFormat(entry.start_date),
        end_date_formatted: current ? 'Till Present' : safeFormat(entry.end_date),
      };
    },

    applyAiLicenses(licenses) {
      if (!Array.isArray(licenses) || !licenses.length) {
        return 0;
      }

      const normalizedLicenses = licenses
        .map((license, index) => ({
          uid: license.uid || `${Date.now()}-${index}`,
          type: license.type || '',
          number: license.number || '',
          issueDate: license.issueDate || null,
          expiry: license.expiry || null,
          license_document: null,
          pending_license_document: null,
          license_path: null,
          license_id: null,
          license_name: null,
          licensePreview: null,
        }))
        .filter((license) => [license.type, license.number, license.expiry].some((value) => this.hasValue(value)));

      if (!normalizedLicenses.length) {
        return 0;
      }

      const existingLicenses = Array.isArray(this.basicInfo.licenses) ? this.basicInfo.licenses : [];
      const meaningfulExisting = existingLicenses.filter((license) => [
        license.type,
        license.number,
        license.expiry,
        license.license_path,
      ].some((value) => this.hasValue(value)));

      let added = 0;
      const merged = [...meaningfulExisting];

      normalizedLicenses.forEach((license) => {
        const duplicate = merged.some((existing) => [
          existing.type,
          existing.number,
          existing.expiry,
        ].map((value) => this.normalizedValue(value)).join('|') === [
          license.type,
          license.number,
          license.expiry,
        ].map((value) => this.normalizedValue(value)).join('|'));

        if (!duplicate) {
          merged.push(license);
          added++;
        }
      });

      if (merged.length) {
        this.$set(this.basicInfo, 'licenses', merged);
      }

      return added;
    },

    normalizeSortCode(value) {
      const digits = `${value || ''}`.replace(/\D/g, '').slice(0, 6);
      if (digits.length <= 2) return digits;
      if (digits.length <= 4) return digits.replace(/(\d{2})(\d+)/, '$1-$2');
      return digits.replace(/(\d{2})(\d{2})(\d+)/, '$1-$2-$3');
    },

    applyUploadedAiFiles(files) {
      if (!Array.isArray(files) || !files.length) {
        return;
      }

      if (!Array.isArray(this.basicInfo.files)) {
        this.$set(this.basicInfo, 'files', []);
      }

      files.forEach((file) => {
        if (!this.basicInfo.files.some((existing) => existing.id === file.id)) {
          this.basicInfo.files.push(file);
        }

        if (Array.isArray(this.applicantFiles) && !this.applicantFiles.some((existing) => existing.id === file.id)) {
          this.applicantFiles.push(file);
        }

        if (this.missingDocuments && this.missingDocuments.length > 0) {
          this.missingDocuments = this.missingDocuments.filter((type) => type !== file.type);
        }

        if (file.type === 'passport') {
          this.$set(this.basicInfo, 'passport_path', file.file_path);
          this.$set(this.basicInfo, 'passport_id', file.id);
          this.$set(this.basicInfo, 'passport_name', file.file_name);
        }

        if (file.type === 'driving_license') {
          this.$set(this.basicInfo, 'is_driving_license', 'yes');
          this.$set(this.basicInfo, 'driving_license_path', file.file_path);
          this.$set(this.basicInfo, 'driving_license_id', file.id);
          this.$set(this.basicInfo, 'driving_license_name', file.file_name);
        }

        if (file.type === 'siaLicense') {
          this.attachSiaFileToFirstLicense(file);
        }
      });
    },

    attachSiaFileToFirstLicense(file) {
      const licenses = Array.isArray(this.basicInfo.licenses) && this.basicInfo.licenses.length
        ? [...this.basicInfo.licenses]
        : [{
          uid: `${Date.now()}-sia`,
          type: '',
          number: '',
          issueDate: null,
          expiry: null,
          license_document: null,
          pending_license_document: null,
        }];

      const index = licenses.findIndex((license) => !license.license_id);
      const targetIndex = index === -1 ? 0 : index;
      licenses[targetIndex] = {
        ...licenses[targetIndex],
        license_path: file.file_path,
        license_id: file.id,
        license_name: file.file_name,
        licensePreview: file.file_path,
        license_document: null,
        pending_license_document: null,
      };

      this.$set(this.basicInfo, 'licenses', licenses);
    },

    normalizeAiAddress(address) {
      const normalized = {
        ...address,
        current_status: !!address.current_status,
      };
      if (normalized.current_status) normalized.to_date = null;

      normalized.address = [
        normalized.door_number,
        normalized.street,
        normalized.city,
        normalized.county,
        normalized.post_code,
        normalized.country,
      ].filter(this.hasValue).join(', ');

      return normalized;
    },

    /* ----- helpers ------------------------------------------------------- */

    hasValue(value) {
      return value !== null && value !== undefined && String(value).trim() !== '';
    },

    normalizedValue(value) {
      return this.hasValue(value) ? String(value).trim().toLowerCase() : '';
    },

    hasAddressIdentity(address) {
      return [address.street, address.city, address.post_code, address.country].some(this.hasValue);
    },

    hasDuplicateAddress(candidate) {
      const key = [candidate.street, candidate.city, candidate.post_code, candidate.from_date]
        .map(this.normalizedValue)
        .join('|');

      return this.addressInfo.some((address) => [address.street, address.city, address.post_code, address.from_date]
        .map(this.normalizedValue)
        .join('|') === key);
    },

    hasDuplicateHistory(candidate) {
      const organization = candidate.type === 0 ? candidate.company_name : candidate.institute;
      const key = [candidate.type, organization, candidate.start_date, candidate.end_date]
        .map(this.normalizedValue)
        .join('|');

      return this.candidateHistoryInfo.some((history) => {
        const existingOrganization = history.type === 0 ? history.company_name : history.institute;
        return [history.type, existingOrganization, history.start_date, history.end_date]
          .map(this.normalizedValue)
          .join('|') === key;
      });
    },

    hasDuplicateTraining(candidate) {
      const key = [candidate.title, candidate.awarding_body, candidate.award_date]
        .map(this.normalizedValue)
        .join('|');

      return this.trainingInfo.some((training) => [training.title, training.awarding_body, training.award_date]
        .map(this.normalizedValue)
        .join('|') === key);
    },
  },
};
